import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  TFileUploadResponse,
  TUploadResult,
} from '@prlw/libs/file-upload/types';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService<T = TUploadResult> {
  files$ = new BehaviorSubject<{ [key: string]: TFileUploadResponse<T> }>({});

  attachedFiles$ = this.files$.pipe(
    map((obj) =>
      Object.keys(obj).map((key) => ({
        fileName: key,
        ...obj[key],
      })),
    ),
  );

  public attachFile(
    file: File,
    fn: (formData: FormData) => Observable<TFileUploadResponse<T>>,
  ): Observable<TFileUploadResponse<T>> {
    return of(file).pipe(
      switchMap((fileToCheck) => {
        const error = this.isNotValid(fileToCheck);
        if (error) {
          return of({
            done: true,
            pending: false,
            error,
          });
        }
        const formData = new FormData();
        formData.append('file', fileToCheck, fileToCheck.name);
        return fn(formData);
      }),
      tap((event) => {
        this.files$.next({
          ...this.files$.getValue(),
          [file.name]: event,
        });
      }),
    );
  }

  public removeAttachedFile(fileName: string): void {
    const files = this.files$.getValue();
    // eslint-disable-next-line
    delete files[fileName];
    this.files$.next(files);
  }

  private isNotValid(file: File): string | null {
    const ALLOWED_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'mp4'];
    const MAX_FILE_SIZE_BINARY = 10485760;
    const errors: string[] = [];
    if (file.size > MAX_FILE_SIZE_BINARY) {
      errors.push('Файл больше 10 Мб.');
    }

    if (
      !ALLOWED_FILE_EXTENSIONS.includes(
        //check mime
        this.getExtension(file.name.toLowerCase()),
      )
    ) {
      errors.push(
        `Недопустимый формат файла. Допустимые форматы: ${ALLOWED_FILE_EXTENSIONS.join(
          ', ',
        )}.`,
      );
    }

    return !errors.length ? null : errors.join(' ');
  }

  private getExtension(filename: string): string {
    return filename.substring(filename.lastIndexOf('.') + 1);
  }
}
